import {
  usePaginatedResource,
  useResourceItem,
  useCreateResource,
  useEditResource,
  useDeleteResource,
} from "./useCrudResource"

import {
  getCustomers,
  getCustomer,
  createCustomer,
  editCustomer,
  deleteCustomer,
  getAllCustomers,
  getTrashedCustomers,
  restoreCustomer,
  forceDeleteCustomer,
  getCustomerBySlug,
} from "api/customerApi"

import {
  useIsFetching,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query"
import { useMemo, useCallback } from "react"
import {
  buildCustomerSuggestionBuckets,
  formatCustomerTypeLabel,
  getCustomerDepartmentText,
  getCustomerOrganizationText,
  getCustomerTypeFieldMeta,
  resolveCustomerTypeKey,
} from "helpers/customerOnboarding"

export const CUSTOMER_QUERY_KEY = "customer"

// -------------------- FETCH PAGINATED --------------------
export const useCustomers = (params = {}, enabled = true) => {
  return usePaginatedResource(CUSTOMER_QUERY_KEY, getCustomers, params, enabled)
}

// -------------------- FETCH SINGLE ITEM --------------------
export const useCustomer = (id, enabled = true) => {
  return useResourceItem(CUSTOMER_QUERY_KEY, getCustomer, id, enabled)
}

// -------------------- FETCH BY SLUG --------------------
export const useCustomerBySlug = (slug, enabled = true) => {
  return useResourceItem(
    [CUSTOMER_QUERY_KEY, "slug"],
    getCustomerBySlug,
    slug,
    enabled
  )
}

// -------------------- CREATE --------------------
export const useCreateCustomer = () => {
  return useCreateResource(createCustomer, CUSTOMER_QUERY_KEY)
}

// -------------------- EDIT --------------------
export const useEditCustomer = () => {
  return useEditResource(editCustomer, CUSTOMER_QUERY_KEY)
}

// -------------------- DELETE (Soft Delete) --------------------
export const useDeleteCustomer = () => {
  return useDeleteResource(deleteCustomer, CUSTOMER_QUERY_KEY)
}

// -------------------- FORCE DELETE (Permanent Delete) --------------------
export const useForceDeleteCustomer = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: forceDeleteCustomer,
    onSuccess: (_, id) => {
      // Remove from trashed list cache
      queryClient.setQueriesData([CUSTOMER_QUERY_KEY, "trashed"], old => {
        if (!old || !old.data?.data) return old
        const updated = structuredClone(old)
        updated.data.data = updated.data.data.filter(item => item.id !== id)
        updated.data.total = Math.max(0, (updated.data.total || 1) - 1)
        return updated
      })

      // Remove from any cached customer record
      queryClient.removeQueries([CUSTOMER_QUERY_KEY, id])
    },
  })
}

// -------------------- ADDITIONAL --------------------

// ✅ Get all customers without pagination
export const useAllCustomers = (params = {}) => {
  return usePaginatedResource(
    CUSTOMER_QUERY_KEY,
    getAllCustomers,
    { ...params, all: true },
    true
  )
}

// ✅ Get trashed (soft-deleted) customers
export const useTrashedCustomers = (params = {}) => {
  return usePaginatedResource(
    [CUSTOMER_QUERY_KEY, "trashed"],
    getTrashedCustomers,
    params,
    true
  )
}

// ✅ Restore a trashed customer
export const useRestoreCustomer = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: restoreCustomer,
    onSuccess: (res, id) => {
      const restoredItem = res?.data
      if (!restoredItem) return

      // Remove from trashed cache
      queryClient.setQueriesData([CUSTOMER_QUERY_KEY, "trashed"], old => {
        if (!old || !old.data?.data) return old
        const updated = structuredClone(old)
        updated.data.data = updated.data.data.filter(item => item.id !== id)
        updated.data.total = Math.max(0, (updated.data.total || 1) - 1)
        return updated
      })

      // Add to main list cache
      queryClient.setQueriesData([CUSTOMER_QUERY_KEY], old => {
        if (!old || !old.data?.data) return old
        const updated = structuredClone(old)
        updated.data.data = [restoredItem, ...updated.data.data]
        updated.data.total = (updated.data.total || 0) + 1
        return updated
      })

      // Update individual cache
      queryClient.setQueryData([CUSTOMER_QUERY_KEY, id], restoredItem)
    },
  })
}

// Customer info
export const useCustomerInfo = () => {
  const { data } = useAllCustomers({ per_page: 500 })
  const customers = useMemo(() => data?.data?.data ?? data?.data ?? [], [data])
  const options = useMemo(() => buildCustomerSuggestionBuckets(customers), [customers])

  const infoFormatter = customerInfo => {
    const customerTypeKey = resolveCustomerTypeKey(
      customerInfo.customer_type,
    )
    const customerTypeLabel = formatCustomerTypeLabel(customerTypeKey)
    const typeMeta = getCustomerTypeFieldMeta(customerTypeKey)

    return {
      ...customerInfo,
      name: customerInfo.name || "-",
      mobile: customerInfo.mobile || "-",
      email: customerInfo.email || "-",
      designation: customerInfo.designation || "-",
      customer_type: customerTypeLabel || customerInfo.customer_type || "-",
      organization_label: typeMeta.organizationLabel,
      department_label: typeMeta.departmentLabel,
      organization: getCustomerOrganizationText(customerInfo) || "-",
      department_text: getCustomerDepartmentText(customerInfo) || "-",
      full_address: customerInfo.full_address || "-",
    }
  }

  const getCustomerRickText = customerInfo => {
    if (!customerInfo || !Object.keys(customerInfo).length) return ""

    const info = infoFormatter(customerInfo)

    return `
<strong>Name:</strong> ${info.name ?? "" ?? ""}<br/>
<strong>Mobile:</strong> ${info.mobile ?? "" ?? ""}<br/>
<strong>Email:</strong> ${info.email ?? "" ?? ""}<br/>
<strong>Designation:</strong> ${info.designation ?? "" ?? ""}<br/>
<strong>${info.department_label}:</strong> ${info.department_text ?? "" ?? ""}<br/>
<strong>${info.organization_label}:</strong> ${info.organization ?? "" ?? ""}<br/>
<strong>Customer Type:</strong> ${info.customer_type ?? "" ?? ""}<br/>
<strong>Full Address:</strong> ${info.full_address ?? "" ?? ""}
    `.trim()
  }

  const getQuotationCustomerRichText = (customerInfo = {}) => {
    if (!customerInfo || !Object.keys(customerInfo).length) return ""

    const info = infoFormatter(customerInfo)

    const lines = [
      info.name ?? "",
      [info.designation ?? "", info.department_text ?? ""]
        .filter(v => v && v !== "-")
        .join(", ")
        .trim(),
      info.organization ?? "",
      info.full_address ?? "",
    ].filter(v => v && v.trim() !== "")

    return lines.join("<br>")
  }

  const getCustomerInfo = (customerInfo = {}) => {
    if (!customerInfo || !Object.keys(customerInfo).length) return ""

    return infoFormatter(customerInfo)
  }

  return {
    getCustomerRickText,
    getQuotationCustomerRichText,
    getCustomerInfo,
    customer: options,
    options,
  }
}

// Function version (for programmatic access)
export const useCustomerSearchAPI = ({ all = true, limit = 3 } = {}) => {
  const queryClient = useQueryClient()

  const allFlag = typeof all === "boolean" ? all : true
  const limitFlag = limit ?? 3

  // Function uses React Query cache internally
  const customerSearchAPI = useCallback(
    async searchTerm => {
      const queryKey = [
        "customer-searching",
        {
          search: searchTerm,
          all: allFlag,
          limit: limitFlag,
        },
      ]

      // ✅ 1. Try to get cached data first
      const cached = queryClient.getQueryData(queryKey)
      if (cached) return cached

      // ✅ 2. If not cached, fetch and cache manually
      const data = await queryClient.fetchQuery({
        queryKey,
        queryFn: () =>
          getCustomers({ search: searchTerm, all: allFlag, limit: limitFlag }),
      })

      return data
    },
    [queryClient, allFlag, limitFlag]
  )

  // React Query tracks global fetch state for this key
  const isFetching = useIsFetching({ queryKey: ["customer-searching"] }) > 0

  return { customerSearchAPI, loading: isFetching }
}
